import { formatTime, parseTime, pushDataLayerEvent } from './utils.js';


export function initializeTrimControls() {
    const video = document.getElementById('video');
    const trimSlider = document.getElementById('trimSlider');
    const trimHandleStart = document.getElementById('trimHandleStart');
    const trimHandleEnd = document.getElementById('trimHandleEnd');
    const trimProgress = document.getElementById('trimProgress');
    const startTimeInput = document.getElementById('startTime');
    const endTimeInput = document.getElementById('endTime');
    const previewTrimBtn = document.getElementById('previewTrimBtn');

    let videoDuration = 0;
    let isDraggingStart = false;
    let isDraggingEnd = false;
    let startTime = 0;
    let endTime = 0;
    let currentZoomLevel = 1; // Track current zoom level
    const MAX_ZOOM_LEVEL = 10; // Maximum zoom level
    const ZOOM_STEP = 0.5; // How much to zoom in each step

    // Initialize trim controls when video metadata is loaded
    video.addEventListener('loadedmetadata', () => {
        videoDuration = video.duration;
        endTime = videoDuration;
        currentZoomLevel = 1; // Track current zoom level
        // Format and set initial end time value
        endTimeInput.value = formatTime(videoDuration);
        startTimeInput.value = '00:00:00.000';
        startTime = 0;

        // Position end handle at the end (adjust for transform)
        trimHandleEnd.style.left = '100%';
        trimHandleStart.style.left = '0%';
        updateProgressBar();
    });

    // Update progress bar between handles
    function updateProgressBar() {
        video.currentTime = startTime;
        const startPercent = (startTime / videoDuration) * 100;
        const endPercent = (endTime / videoDuration) * 100;

        trimProgress.style.left = `${startPercent}%`;
        trimProgress.style.width = `${endPercent - startPercent}%`;
    }

    // Handle drag position calculation for both mouse and touch events
    function calculateDragPosition(clientX) {
        const sliderRect = trimSlider.getBoundingClientRect();
        const percentage = Math.min(Math.max(0, (clientX - sliderRect.left) / sliderRect.width), 1);
        return percentage * videoDuration;
    }

    // MOUSE EVENTS
    // Handle mouse down on start handle
    trimHandleStart.addEventListener('mousedown', (e) => {
        e.preventDefault();
        isDraggingStart = true;
    });

    // Handle mouse down on end handle
    trimHandleEnd.addEventListener('mousedown', (e) => {
        e.preventDefault();
        isDraggingEnd = true;
    });

    // Handle mouse move for both handles
    document.addEventListener('mousemove', (e) => {
        if (!isDraggingStart && !isDraggingEnd) return;

        const newTime = calculateDragPosition(e.clientX);

        if (isDraggingStart) {
            if (newTime < endTime) {
                startTime = newTime;
                trimHandleStart.style.left = `${(startTime / videoDuration) * 100}%`;
                startTimeInput.value = formatTime(startTime);
                updateProgressBar();
            }
        } else if (isDraggingEnd) {
            if (newTime > startTime) {
                endTime = newTime;
                trimHandleEnd.style.left = `${(endTime / videoDuration) * 100}%`;
                endTimeInput.value = formatTime(endTime);
                updateProgressBar();
            }
        }
    });

    // Handle mouse up to stop dragging
    document.addEventListener('mouseup', () => {
        isDraggingStart = false;
        isDraggingEnd = false;
    });

    // TOUCH EVENTS
    // Handle touch start on start handle
    trimHandleStart.addEventListener('touchstart', (e) => {
        e.preventDefault(); // Prevent scrolling when touching the handle
        isDraggingStart = true;
    });

    // Handle touch start on end handle
    trimHandleEnd.addEventListener('touchstart', (e) => {
        e.preventDefault(); // Prevent scrolling when touching the handle
        isDraggingEnd = true;
    });

    // Handle touch move for both handles
    document.addEventListener('touchmove', (e) => {
        if (!isDraggingStart && !isDraggingEnd) return;

        // Get the first touch point
        const touch = e.touches[0];
        const newTime = calculateDragPosition(touch.clientX);

        if (isDraggingStart) {
            if (newTime < endTime) {
                startTime = newTime;
                trimHandleStart.style.left = `${(startTime / videoDuration) * 100}%`;
                startTimeInput.value = formatTime(startTime);
                updateProgressBar();
            }
        } else if (isDraggingEnd) {
            if (newTime > startTime) {
                endTime = newTime;
                trimHandleEnd.style.left = `${(endTime / videoDuration) * 100}%`;
                endTimeInput.value = formatTime(endTime);
                updateProgressBar();
            }
        }
    }, { passive: false }); // Important for iOS to allow preventDefault

    // Handle touch end to stop dragging
    document.addEventListener('touchend', () => {
        isDraggingStart = false;
        isDraggingEnd = false;
    });

    // Handle click/tap directly on the slider (jump to position)
    trimSlider.addEventListener('click', (e) => {
        // Only handle direct clicks on the slider, not on the handles
        if (e.target === trimSlider || e.target === trimProgress) {
            const newTime = calculateDragPosition(e.clientX);

            // Determine which handle to move based on proximity
            const distToStart = Math.abs(newTime - startTime);
            const distToEnd = Math.abs(newTime - endTime);

            if (distToStart < distToEnd) {
                // Move start handle
                startTime = Math.min(newTime, endTime - 1);
                trimHandleStart.style.left = `${(startTime / videoDuration) * 100}%`;
                startTimeInput.value = formatTime(startTime);
            } else {
                // Move end handle
                endTime = Math.max(newTime, startTime + 1);
                trimHandleEnd.style.left = `${(endTime / videoDuration) * 100}%`;
                endTimeInput.value = formatTime(endTime);
            }

            updateProgressBar();
        }
    });

    // Also support tapping on the slider for mobile
    trimSlider.addEventListener('touchend', (e) => {
        // Prevent default only if we're not dragging (to allow proper handle dragging)
        if (!isDraggingStart && !isDraggingEnd) {
            // Only handle direct taps on the slider, not on the handles
            if (e.target === trimSlider || e.target === trimProgress) {
                const touch = e.changedTouches[0];
                const newTime = calculateDragPosition(touch.clientX);

                // Determine which handle to move based on proximity
                const distToStart = Math.abs(newTime - startTime);
                const distToEnd = Math.abs(newTime - endTime);

                if (distToStart < distToEnd) {
                    // Move start handle
                    startTime = Math.min(newTime, endTime - 1);
                    trimHandleStart.style.left = `${(startTime / videoDuration) * 100}%`;
                    startTimeInput.value = formatTime(startTime);
                } else {
                    // Move end handle
                    endTime = Math.max(newTime, startTime + 1);
                    trimHandleEnd.style.left = `${(endTime / videoDuration) * 100}%`;
                    endTimeInput.value = formatTime(endTime);
                }

                updateProgressBar();
            }
        }
    });

    // Handle manual time input for start time
    startTimeInput.addEventListener('input', () => {
        // Allow typing without validation
        // Validation will happen on blur
    });

    // Handle manual time input for end time
    endTimeInput.addEventListener('input', () => {
        // Allow typing without validation
        // Validation will happen on blur
    });

    // Handle blur event (when input loses focus) for both inputs
    startTimeInput.addEventListener('blur', () => {
        // Validate time format (HH:MM:SS.mmm)
        const timeFormat = /^\d{2}:\d{2}:\d{2}\.\d{3}$/;
        if (!timeFormat.test(startTimeInput.value)) {
            // If format is invalid, reset to previous valid value
            startTimeInput.value = formatTime(startTime);
            return;
        }

        const inputTime = parseTime(startTimeInput.value);
        if (inputTime !== null && inputTime >= 0 && inputTime <= videoDuration) {
            if (inputTime < endTime) {
                startTime = inputTime;
                trimHandleStart.style.left = `${(startTime / videoDuration) * 100}%`;
                updateProgressBar();
            } else {
                // Reset to previous valid value if new time is greater than or equal to end time
                startTimeInput.value = formatTime(startTime);
            }
        } else {
            // Reset to previous valid value if time is invalid
            startTimeInput.value = formatTime(startTime);
        }
    });

    endTimeInput.addEventListener('blur', () => {
        // Validate time format (HH:MM:SS.mmm)
        const timeFormat = /^\d{2}:\d{2}:\d{2}\.\d{3}$/;
        if (!timeFormat.test(endTimeInput.value)) {
            // If format is invalid, reset to previous valid value
            endTimeInput.value = formatTime(endTime);
            return;
        }

        const inputTime = parseTime(endTimeInput.value);
        if (inputTime !== null && inputTime >= 0 && inputTime <= videoDuration) {
            if (inputTime > startTime) {
                endTime = inputTime;
                trimHandleEnd.style.left = `${(endTime / videoDuration) * 100}%`;
                updateProgressBar();
            } else {
                // Reset to previous valid value if new time is less than or equal to start time
                endTimeInput.value = formatTime(endTime);
            }
        } else {
            // Reset to previous valid value if time is invalid
            endTimeInput.value = formatTime(endTime);
        }
    });

    // Preview trim button - play video from start to end point
    previewTrimBtn.addEventListener('click', () => {
        video.currentTime = startTime;
        video.play();

        // Stop video when reaching end time
        const checkTime = () => {
            if (video.currentTime >= endTime) {
                video.pause();
                video.removeEventListener('timeupdate', checkTime);
            }
        };

        video.addEventListener('timeupdate', checkTime);
    });

    // Add a timeline indicator for current playback position
    const createTimelineIndicator = () => {
        const indicator = document.createElement('div');
        indicator.className = 'timeline-indicator';
        indicator.style.cssText = `
    position: absolute;
    top: -10px;
    bottom: -10px;
    width: 2px;
    background-color: #ff0000;
    z-index: 3;
    pointer-events: none;
    display: none;
`;
        trimSlider.appendChild(indicator);
        return indicator;
    };

    const timelineIndicator = createTimelineIndicator();

    // Update timeline indicator when video is playing
    video.addEventListener('timeupdate', () => {
        if (video.currentTime >= startTime && video.currentTime <= endTime) {
            timelineIndicator.style.display = 'block';

            // Calculate position as percentage of video duration
            const percentage = (video.currentTime / videoDuration) * 100;

            // Set the position using percentage (consistent with other elements)
            timelineIndicator.style.left = `${percentage}%`;
        } else {
            timelineIndicator.style.display = 'none';
        }
    });

    // Hide timeline indicator when video is paused
    video.addEventListener('pause', () => {
        timelineIndicator.style.display = 'none';
    });

    // Frame Navigation
    const prevFrameBtn = document.getElementById('prevFrameBtn');
    const nextFrameBtn = document.getElementById('nextFrameBtn');

    // Add keyboard controls for frame navigation
    document.addEventListener('keydown', (e) => {
        // Only handle if the video element is focused or if we're not focused on an input
        if (document.activeElement === video || document.activeElement.tagName !== 'INPUT') {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                navigateFrame('prev');
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                navigateFrame('next');
            }
        }
    });

    // Zoom Controls
    const zoomInBtn = document.getElementById('zoomInBtn');
    const resetZoomBtn = document.getElementById('resetZoomBtn');

    // Frame navigation event listeners
    prevFrameBtn.addEventListener('click', () => {
        navigateFrame('prev');
    });

    nextFrameBtn.addEventListener('click', () => {
        navigateFrame('next');
    });

    // Add touch events for frame navigation
    prevFrameBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        navigateFrame('prev');
    });

    nextFrameBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        navigateFrame('next');
    });

    // Zoom control event listeners
    zoomInBtn.addEventListener('click', () => {
        adjustZoom('in');
    });

    resetZoomBtn.addEventListener('click', () => {
        resetZoom();
    });

    // Add touch events for zoom controls
    zoomInBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        adjustZoom('in');
    });

    resetZoomBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        resetZoom();
    });

    // Add copy to end time button functionality
    const copyToEndBtn = document.getElementById('copyToEndBtn');
    copyToEndBtn.addEventListener('click', () => {
        const startTimeValue = parseTime(startTimeInput.value);
        if (startTimeValue !== null && startTimeValue >= 0 && startTimeValue <= videoDuration) {
            endTime = startTimeValue;
            endTimeInput.value = formatTime(endTime);
            trimHandleEnd.style.left = `${(endTime / videoDuration) * 100}%`;
            updateProgressBar();
        }
    });

    // Add touch event for copy button
    copyToEndBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        const startTimeValue = parseTime(startTimeInput.value);
        if (startTimeValue !== null && startTimeValue >= 0 && startTimeValue <= videoDuration) {
            endTime = startTimeValue;
            endTimeInput.value = formatTime(endTime);
            trimHandleEnd.style.left = `${(endTime / videoDuration) * 100}%`;
            updateProgressBar();
        }
    });

    /**
     * Navigate to the previous or next frame
     * @param {string} direction - 'prev' or 'next'
     */
    function navigateFrame(direction) {
        // Pause video if playing
        if (!video.paused) {
            video.pause();
        }

        // Calculate new time based on direction (33ms step)
        const TIME_STEP = 0.033; // 33ms in seconds
        let newTime;
        if (direction === 'prev') {
            newTime = Math.max(0, video.currentTime - TIME_STEP);
        } else {
            newTime = Math.min(videoDuration, video.currentTime + TIME_STEP);
        }

        // Update video time
        video.currentTime = newTime;

        // Update timeline indicator
        const percentage = (newTime / videoDuration) * 100;
        timelineIndicator.style.left = `${percentage}%`;
        timelineIndicator.style.display = 'block';

        // Update the appropriate handle based on proximity
        const distToStart = Math.abs(newTime - startTime);
        const distToEnd = Math.abs(newTime - endTime);

        if (distToStart < distToEnd) {
            // Update start handle
            startTime = newTime;
            trimHandleStart.style.left = `${(startTime / videoDuration) * 100}%`;
            startTimeInput.value = formatTime(startTime);
        } else {
            // Update end handle
            endTime = newTime;
            trimHandleEnd.style.left = `${(endTime / videoDuration) * 100}%`;
            endTimeInput.value = formatTime(endTime);
        }
        updateProgressBar();
    }

    /**
     * Adjust the zoom level of the timeline
     * @param {string} direction - 'in' or 'out'
     */
    function adjustZoom(direction) {
        if (direction === 'in' && currentZoomLevel < MAX_ZOOM_LEVEL) {
            currentZoomLevel += ZOOM_STEP;
            
            // Calculate the center point of the current view
            const centerPoint = (startTime + endTime) / 2;
            
            // Update the timeline scale
            trimSlider.style.transform = `scaleX(${currentZoomLevel})`;
            trimSlider.style.transformOrigin = `${(centerPoint / videoDuration) * 100}% 50%`;
            
            // Counter-scale the handles and timeline indicator to maintain their size
            const counterScale = 1 / currentZoomLevel;
            trimHandleStart.style.transform = `translateX(-50%) scaleX(${counterScale})`;
            trimHandleEnd.style.transform = `translateX(-50%) scaleX(${counterScale})`;
            timelineIndicator.style.transform = `scaleX(${counterScale})`;
            
            // Adjust handle positions to maintain their relative positions
            const startPercent = (startTime / videoDuration) * 100;
            const endPercent = (endTime / videoDuration) * 100;
            
            trimHandleStart.style.left = `${startPercent}%`;
            trimHandleEnd.style.left = `${endPercent}%`;
            
            // Update progress bar
            updateProgressBar();
            
            // Ensure handles stay visible and container maintains proper size
            const container = trimSlider.parentElement;
            container.style.overflow = 'hidden';
            container.style.width = '100%';
            container.style.position = 'relative';
            
            // Add padding to container to prevent handles from being cut off
            const handleWidth = 20; // Approximate width of handle
            container.style.paddingLeft = `${handleWidth}px`;
            container.style.paddingRight = `${handleWidth}px`;
            
            // Add smooth transition
            trimSlider.style.transition = 'transform 0.3s ease';
            trimHandleStart.style.transition = 'transform 0.3s ease';
            trimHandleEnd.style.transition = 'transform 0.3s ease';
            timelineIndicator.style.transition = 'transform 0.3s ease';
        }
    }

    /**
     * Reset the zoom level to default
     */
    function resetZoom() {
        currentZoomLevel = 1;
        trimSlider.style.transform = 'scaleX(1)';
        trimSlider.style.transformOrigin = '0% 50%';
        
        // Reset handle and timeline indicator transforms
        trimHandleStart.style.transform = 'translateX(-50%) scaleX(1)';
        trimHandleEnd.style.transform = 'translateX(-50%) scaleX(1)';
        timelineIndicator.style.transform = 'scaleX(1)';
        
        // Reset container styles
        const container = trimSlider.parentElement;
        container.style.overflow = 'hidden';
        container.style.width = '100%';
        container.style.position = 'relative';
        container.style.paddingLeft = '0';
        container.style.paddingRight = '0';
        
        trimSlider.style.transition = 'transform 0.3s ease';
        trimHandleStart.style.transition = 'transform 0.3s ease';
        trimHandleEnd.style.transition = 'transform 0.3s ease';
        timelineIndicator.style.transition = 'transform 0.3s ease';
    }
}